/**
 * Login page. Email + password only (Supabase Auth handles credentials).
 * No self-registration, no complex account management — accounts are
 * still created directly via the Supabase dashboard or the Admin Users
 * page. Password reset, however, IS handled here explicitly: an earlier
 * version of this app assumed "Supabase Auth's own built-in recovery"
 * would just work with zero app-side code, which was wrong in a real,
 * reported way — the recovery email's destination depends on the
 * Supabase project's Site URL setting (not the Redirect URLs allowlist,
 * which only governs explicit redirectTo calls), and even when that
 * destination is correct, nothing existed to actually let a user set a
 * new password once they landed there. Calling resetPasswordForEmail
 * with an explicit `redirectTo` here removes the Site URL dependency
 * entirely — see ResetPasswordPage.tsx for the other half of this fix.
 */

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabaseClient';

export function LoginPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [mode, setMode] = useState<'sign_in' | 'forgot_password'>('sign_in');
  const [resetEmail, setResetEmail] = useState('');
  const [resetSubmitting, setResetSubmitting] = useState(false);
  const [resetMessage, setResetMessage] = useState<{ text: string; tone: 'success' | 'error' } | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });

    setSubmitting(false);

    if (signInError) {
      setError('That email and password combination didn\u2019t work. Please try again.');
      return;
    }

    navigate('/', { replace: true });
  }

  async function handleResetRequest(e: FormEvent) {
    e.preventDefault();
    setResetMessage(null);
    setResetSubmitting(true);

    // The explicit redirectTo is the actual fix: without it, Supabase
    // builds the recovery link from the project's Site URL setting,
    // which may not point at this app at all (or may point at a
    // production URL that doesn't exist yet) — and changing the
    // Redirect URLs allowlist, which only matters when redirectTo is
    // passed, has no effect on that default. Passing it here makes the
    // destination correct regardless of what Site URL happens to be set
    // to in the Supabase dashboard.
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(resetEmail, {
      redirectTo: `${window.location.origin}/reset-password`,
    });

    setResetSubmitting(false);

    if (resetError) {
      setResetMessage({ text: "Something went wrong sending that email. Please try again.", tone: 'error' });
      return;
    }

    // Deliberately the same message whether or not the email exists —
    // confirming/denying an email's existence on a password-reset form
    // is a real, well-known account-enumeration risk, and there's no
    // legitimate reason for a coordinator-facing tool to take that risk.
    setResetMessage({
      text: "If an account exists for that email, a reset link is on its way. It can take a minute or two to arrive.",
      tone: 'success',
    });
  }

  if (mode === 'forgot_password') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-evergreen-700 px-6">
        <div className="w-full max-w-md">
          <div className="mb-10 text-center">
            <p className="font-body text-xs uppercase tracking-[0.2em] text-gold-300">
              The Venue at Lilly Lou&rsquo;s
            </p>
            <h1 className="mt-2 font-display text-4xl font-medium text-ivory-50">
              The Pines Planner
            </h1>
          </div>

          <form onSubmit={handleResetRequest} className="rounded-xl bg-ivory-50 p-8 shadow-elevated">
            <h2 className="font-display text-xl text-ink-700">Reset your password</h2>
            <p className="mt-1 font-body text-sm text-ink-400">
              Enter your email and we&rsquo;ll send you a link to set a new password.
            </p>

            <div className="mt-6">
              <label htmlFor="reset-email" className="font-body text-sm font-medium text-ink-600">
                Email
              </label>
              <input
                id="reset-email"
                type="email"
                autoComplete="email"
                required
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
              />
            </div>

            {resetMessage && (
              <p
                role="status"
                className={`mt-4 font-body text-sm ${resetMessage.tone === 'error' ? 'text-danger' : 'text-evergreen-600'}`}
              >
                {resetMessage.text}
              </p>
            )}

            <button
              type="submit"
              disabled={resetSubmitting}
              className="mt-6 w-full rounded bg-evergreen-600 px-4 py-2.5 font-body font-medium text-ivory-50 transition-colors hover:bg-evergreen-700 disabled:opacity-60"
            >
              {resetSubmitting ? 'Sending\u2026' : 'Send reset link'}
            </button>

            <button
              type="button"
              onClick={() => {
                setMode('sign_in');
                setResetMessage(null);
              }}
              className="mt-4 w-full font-body text-sm text-ink-400 hover:text-ink-600"
            >
              {'\u2190'} Back to sign in
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-evergreen-700 px-6">
      <div className="w-full max-w-md">
        <div className="mb-10 text-center">
          <p className="font-body text-xs uppercase tracking-[0.2em] text-gold-300">
            The Venue at Lilly Lou&rsquo;s
          </p>
          <h1 className="mt-2 font-display text-4xl font-medium text-ivory-50">
            The Pines Planner
          </h1>
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl bg-ivory-50 p-8 shadow-elevated"
        >
          <h2 className="font-display text-xl text-ink-700">Welcome back</h2>
          <p className="mt-1 font-body text-sm text-ink-400">
            Sign in to plan your next reception layout.
          </p>

          <div className="mt-6 space-y-4">
            <div>
              <label htmlFor="email" className="font-body text-sm font-medium text-ink-600">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
              />
            </div>

            <div>
              <div className="flex items-center justify-between">
                <label htmlFor="password" className="font-body text-sm font-medium text-ink-600">
                  Password
                </label>
                <button
                  type="button"
                  onClick={() => {
                    setError(null);
                    setMode('forgot_password');
                  }}
                  className="font-body text-xs text-evergreen-600 underline-offset-2 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
              <input
                id="password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full rounded border border-ivory-300 bg-white px-3 py-2 font-body text-ink-700 outline-none focus:border-gold-400 focus:ring-2 focus:ring-gold-200"
              />
            </div>
          </div>

          {error && (
            <p role="alert" className="mt-4 font-body text-sm text-danger">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full rounded bg-evergreen-600 px-4 py-2.5 font-body font-medium text-ivory-50 transition-colors hover:bg-evergreen-700 disabled:opacity-60"
          >
            {submitting ? 'Signing in…' : 'Sign in'}
          </button>
        </form>

        <p className="mt-6 text-center font-body text-xs text-ivory-200">
          Need access? Ask your venue administrator to set up your account.
        </p>
      </div>
    </div>
  );
}
